import { c as _c } from "react/compiler-runtime";
import type { PullRequest } from '@/app/pr/[owner]/[repo]/pull/[number]/components/types';
import type { MergeabilityStatus } from '@/lib/hooks/mergeability-status/get-mergeability-status.server';
import { Check, X } from 'lucide-react';
import { getApprovalStatus } from '../merge-dropdown-utils';
import { ReviewerAvatars } from './reviewer-avatars';
type ApprovalsSectionProps = {
  pr: PullRequest;
  mergeabilityStatus: MergeabilityStatus;
};
export function ApprovalsSection(t0) {
  const $ = _c(40);
  const {
    pr,
    mergeabilityStatus
  } = t0;
  const {
    passed: approvalsPassed,
    requiredApprovalCount,
    currentApprovalCount,
    hasBlockingChangeRequests
  } = mergeabilityStatus.approvalStatus;
  if (hasBlockingChangeRequests) {
    let t1;
    if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = <span className="flex items-center justify-center gap-2"><X className="size-4 stroke-red-500" />Changes requested</span>;
      $[0] = t1;
    } else {
      t1 = $[0];
    }
    return t1;
  }
  if (requiredApprovalCount > 0) {
    if (approvalsPassed) {
      let t1;
      if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
        t1 = <Check className="size-4 -translate-y-px stroke-zinc-500" />;
        $[1] = t1;
      } else {
        t1 = $[1];
      }
      const t2 = `${currentApprovalCount} approval${currentApprovalCount !== 1 ? "s" : ""} from ${requiredApprovalCount} required`;
      let t3;
      if ($[2] !== t2) {
        t3 = <span className="flex items-center gap-2">{t1}{t2}</span>;
        $[2] = t2;
        $[3] = t3;
      } else {
        t3 = $[3];
      }
      let t4;
      if ($[4] !== pr.reviewers) {
        t4 = <ReviewerAvatars reviewers={pr.reviewers} />;
        $[4] = pr.reviewers;
        $[5] = t4;
      } else {
        t4 = $[5];
      }
      let t5;
      if ($[6] !== t3 || $[7] !== t4) {
        t5 = <div className="flex items-center gap-2">{t3}{t4}</div>;
        $[6] = t3;
        $[7] = t4;
        $[8] = t5;
      } else {
        t5 = $[8];
      }
      return t5;
    }
    const missingApprovals = Math.max(requiredApprovalCount - currentApprovalCount, 1);
    let t1;
    if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = <X className="size-4 -translate-y-px stroke-red-500" />;
      $[9] = t1;
    } else {
      t1 = $[9];
    }
    const t2 = `Requires ${missingApprovals} more approval${missingApprovals !== 1 ? "s" : ""}`;
    let t3;
    if ($[10] !== t2) {
      t3 = <span className="flex items-center gap-2">{t1}{t2}</span>;
      $[10] = t2;
      $[11] = t3;
    } else {
      t3 = $[11];
    }
    return t3;
  }
  let rerequested;
  let t1;
  if ($[12] !== pr.reviewers) {
    const reviewerStates = pr.reviewers.reduce(_temp, new Map());
    rerequested = new Set(pr.reviewers.filter(_temp2).map(_temp3));
    t1 = Array.from(reviewerStates.values());
    $[12] = pr.reviewers;
    $[13] = rerequested;
    $[14] = t1;
  } else {
    rerequested = $[13];
    t1 = $[14];
  }
  const uniqueReviewers = t1;
  let t2;
  if ($[15] !== uniqueReviewers) {
    t2 = uniqueReviewers.filter(_temp4);
    $[15] = uniqueReviewers;
    $[16] = t2;
  } else {
    t2 = $[16];
  }
  const approvedReviewers = t2;
  const numApprovals = approvedReviewers.length;
  const numRequiredApprovals = requiredApprovalCount;
  let t3;
  if ($[17] !== uniqueReviewers) {
    t3 = uniqueReviewers.filter(_temp5);
    $[17] = uniqueReviewers;
    $[18] = t3;
  } else {
    t3 = $[18];
  }
  const reviewersWhoRequestedChanges = t3;
  const numRequestedChanges = reviewersWhoRequestedChanges.length;
  if (numRequestedChanges > 0 && reviewersWhoRequestedChanges) {
    let t4;
    if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
      t4 = <X className="size-4 stroke-zinc-500" />;
      $[19] = t4;
    } else {
      t4 = $[19];
    }
    let t5;
    if ($[20] !== reviewersWhoRequestedChanges) {
      t5 = <ReviewerAvatars reviewers={reviewersWhoRequestedChanges} />;
      $[20] = reviewersWhoRequestedChanges;
      $[21] = t5;
    } else {
      t5 = $[21];
    }
    const t6 = reviewersWhoRequestedChanges.length === 1 ? `${reviewersWhoRequestedChanges[0]?.login} requested changes` : `Changes requested from ${numRequestedChanges} reviewers`;
    let t7;
    if ($[22] !== t5 || $[23] !== t6) {
      t7 = <span className="flex items-center justify-center gap-2">{t4}{t5}{t6}</span>;
      $[22] = t5;
      $[23] = t6;
      $[24] = t7;
    } else {
      t7 = $[24];
    }
    return t7;
  }
  const approvedButRerequested = approvedReviewers.filter(r_1 => rerequested.has(r_1.login));
  const hasRerequestedReviews = approvedButRerequested.length > 0;
  let t4;
  if ($[25] !== numApprovals || $[26] !== numRequiredApprovals || $[27] !== uniqueReviewers.length) {
    t4 = getApprovalStatus(numApprovals, numRequiredApprovals, uniqueReviewers.length);
    $[25] = numApprovals;
    $[26] = numRequiredApprovals;
    $[27] = uniqueReviewers.length;
    $[28] = t4;
  } else {
    t4 = $[28];
  }
  const status = t4;
  let t5;
  if ($[29] !== status.type) {
    t5 = status.type === "has_approvals" ? <Check className="size-4 -translate-y-px stroke-zinc-500" /> : status.type === "missing_approvals" ? <X className="size-4 -translate-y-px stroke-red-500" /> : <span className="inline-block size-3.5 rounded-full bg-yellow-700" />;
    $[29] = status.type;
    $[30] = t5;
  } else {
    t5 = $[30];
  }
  const icon = t5;
  const t6 = hasRerequestedReviews ? `${numApprovals} approval${numApprovals !== 1 ? "s" : ""} (re-reviews requested)` : status.message;
  let t7;
  if ($[31] !== icon || $[32] !== t6) {
    t7 = <span className="flex items-center gap-2">{icon}{t6}</span>;
    $[31] = icon;
    $[32] = t6;
    $[33] = t7;
  } else {
    t7 = $[33];
  }
  let t8;
  if ($[34] !== pr.reviewers || $[35] !== status.type) {
    t8 = status.type === "has_approvals" && <ReviewerAvatars reviewers={pr.reviewers} />;
    $[34] = pr.reviewers;
    $[35] = status.type;
    $[36] = t8;
  } else {
    t8 = $[36];
  }
  let t9;
  if ($[37] !== t7 || $[38] !== t8) {
    t9 = <div className="flex items-center gap-2">{t7}{t8}</div>;
    $[37] = t7;
    $[38] = t8;
    $[39] = t9;
  } else {
    t9 = $[39];
  }
  return t9;
}
function _temp5(reviewer_1) {
  return reviewer_1.state === "CHANGES_REQUESTED";
}
function _temp4(reviewer_0) {
  return reviewer_0.state === "APPROVED";
}
function _temp3(r_0) {
  return r_0.login;
}
function _temp2(r) {
  return r.state === "REQUESTED_REVIEW";
}
function _temp(acc, reviewer) {
  const existing = acc.get(reviewer.login);
  if (!existing || reviewer.submitted_at && (!existing.submitted_at || reviewer.submitted_at > existing.submitted_at)) {
    acc.set(reviewer.login, reviewer);
  }
  return acc;
}